'use client';

import {
  TypographyCaption,
  TypographyP2,
  typographyVariant,
} from '@heidi/design-system/typography';
import { cn } from '@heidi/utils';
import { ExternalLink } from 'lucide-react';
import type { ReactNode } from 'react';
import { useIntl } from 'react-intl';

import { sourceGroundedMessages } from './sourceGrounded.messages';

import { useArtifactPanelNavigation } from '../../../data/projects/artifacts/useArtifactPanelNavigation';
import type { SourceGroundedReference } from '../../../data/projects/source-grounded-citations';
import {
  kindDisplayName,
  parseSourceGroundedLabel,
} from '../../../data/projects/source-grounded-label';
import {
  isSourceGroundedReferenceNavigable,
  resolveSourceGroundedArtifact,
  type SourceGroundedSessionTabs,
} from '../../../data/projects/source-grounded-navigation';
import { abbreviateSourceLabel, iconForSourceLabel } from './source-grounded-icons';

/**
 * The hover-card shell, supplied by whoever composes this chip.
 *
 * `@/components/ui/CitationPopover` is a shared-UI app component and a package
 * may not name an `@/` path, so the popover is INVERTED: the chip renders the
 * trigger and the card body and the composition point wraps them in the real
 * Radix hover card. That keeps this surface free of both the app path and
 * `@radix-ui/react-hover-card`, which is the design system's dependency and not
 * this package's.
 */
export type RenderCitationPopover = (slots: {
  trigger: ReactNode;
  content: ReactNode;
}) => ReactNode;

export type SourceGroundedCitationChipProps = {
  children?: ReactNode;
  'data-idx'?: string;
  dataIdx?: string;
  referencesById: Map<number, SourceGroundedReference>;
  /**
   * `release-scribe-work-source-citation-chips`, resolved by the composition
   * point — a flag is app-resolved by rule and never read from inside a package.
   */
  compactCitationsEnabled: boolean;
  renderPopover: RenderCitationPopover;
  sessionTabs: SourceGroundedSessionTabs;
};

const parseIdx = (raw: string | undefined): number[] => {
  if (!raw?.trim()) return [];
  return raw
    .split(',')
    .map((part) => Number(part.trim()))
    .filter((n) => Number.isFinite(n) && n > 0);
};

const SourceReferenceCard = ({
  reference,
  onOpen,
  compact,
}: {
  reference: SourceGroundedReference;
  onOpen?: () => void;
  compact: boolean;
}) => {
  const intl = useIntl();
  const parsed = parseSourceGroundedLabel(reference.label);
  const kind = reference.kind ?? parsed.kind;
  const title = reference.title ?? parsed.title;
  const subtitle = reference.subtitle ?? parsed.subtitle;
  const displayTitle =
    compact && kind === 'consult_note'
      ? intl.formatMessage(sourceGroundedMessages.noteTitle)
      : title;
  const Icon = iconForSourceLabel(title);
  const navigable = isSourceGroundedReferenceNavigable(reference);

  return (
    <div className="flex flex-col gap-2 px-3 py-2.5">
      <div className="flex gap-3">
        <div className="flex size-8 shrink-0 items-center justify-center rounded-md bg-background-secondary">
          <Icon className="size-4 text-text-tertiary" aria-hidden />
        </div>
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          {!compact && (
            <TypographyCaption className="uppercase tracking-wide text-text-tertiary">
              {kindDisplayName(intl, kind)}
            </TypographyCaption>
          )}
          <TypographyP2 className="font-medium leading-snug text-text-primary">
            {displayTitle}
          </TypographyP2>
          {subtitle && (
            <TypographyCaption className="text-text-secondary">
              {subtitle}
            </TypographyCaption>
          )}
          {reference.detail && (
            <TypographyCaption className="whitespace-pre-wrap break-words text-text-tertiary">
              {reference.detail}
            </TypographyCaption>
          )}
        </div>
      </div>
      {navigable && onOpen && (
        <button
          type="button"
          data-testid={`source-cite-open-${reference.id}`}
          className={cn(
            'inline-flex items-center justify-center gap-1.5 rounded-md border border-border',
            'bg-surface px-2.5 py-1.5 text-xs font-medium text-text-primary',
            'transition-colors hover:border-border-hover hover:bg-background-secondary'
          )}
          onClick={onOpen}
        >
          <ExternalLink className="size-3.5 shrink-0" aria-hidden />
          {intl.formatMessage(sourceGroundedMessages.openSource)}
        </button>
      )}
    </div>
  );
};

/**
 * Inline source citation chip for routine / WorkChat prose.
 * Kept number-only so citation-heavy prose and tables stay compact.
 */
export const SourceGroundedCitationChip = ({
  'data-idx': dataIdxKebab,
  dataIdx: dataIdxCamel,
  referencesById,
  compactCitationsEnabled,
  renderPopover,
  sessionTabs,
}: SourceGroundedCitationChipProps) => {
  const intl = useIntl();
  const { openArtifact } = useArtifactPanelNavigation();
  const ids = parseIdx(dataIdxKebab ?? dataIdxCamel);
  const resolved = ids
    .map((id) => referencesById.get(id))
    .filter((ref): ref is SourceGroundedReference => ref != null);

  if (resolved.length === 0) {
    return null;
  }

  // The release flag enables the structured-citation experience. Prose-derived
  // fallbacks cannot safely use it because they lack backend-verified metadata.
  const compact =
    compactCitationsEnabled &&
    resolved.every((reference) => reference.provenance === 'render_sources');
  const primary = resolved[0]!;
  const PrimaryIcon = iconForSourceLabel(primary.title ?? primary.label);
  const extraCount = resolved.length - 1;
  const chipLabel = abbreviateSourceLabel(primary.label);
  const ariaLabel =
    resolved.length > 1
      ? intl.formatMessage(sourceGroundedMessages.sourcesAria, {
          ids: ids.join(', '),
          labels: resolved.map((ref) => ref.label).join('; '),
        })
      : intl.formatMessage(sourceGroundedMessages.sourceAria, {
          id: primary.id,
          label: primary.label,
        });

  const openReference = (reference: SourceGroundedReference) => {
    const artifact = resolveSourceGroundedArtifact(reference, sessionTabs);
    if (artifact) openArtifact(artifact);
  };

  // Fragment-wrapped because `renderPopover` is typed `ReactNode` and a React 18
  // component may not return one directly. It adds nothing to the DOM.
  return (
    <>
      {renderPopover({
        trigger: (
          <button
            type="button"
            data-testid="source-cite-chip"
            data-source-cite-id={ids.join(',')}
            className={cn(
              compact
                ? 'mx-0.5 inline-flex rounded border border-border bg-surface px-1.5 py-0.5 align-middle text-text-secondary'
                : 'mx-0.5 inline-flex max-w-48 items-center gap-1 rounded-full border border-border bg-surface py-0.5 pe-2 ps-0.5 text-left align-middle text-text-secondary',
              'transition-colors hover:border-border-hover hover:bg-background-secondary',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus'
            )}
            aria-label={ariaLabel}
            onClick={compact ? () => openReference(primary) : undefined}
          >
            {compact ? (
              <span className={typographyVariant({ variant: 'caption' })}>
                {intl.formatMessage(sourceGroundedMessages.compactCitation, {
                  ids: ids.join(', '),
                })}
              </span>
            ) : (
              <>
                <span
                  className={cn(
                    typographyVariant({ variant: 'captionBold' }),
                    'flex size-4 shrink-0 items-center justify-center rounded-full bg-validation-info-muted tabular-nums text-validation-info'
                  )}
                >
                  {primary.id}
                </span>
                <PrimaryIcon className="size-3 shrink-0 text-text-tertiary" aria-hidden />
                <span
                  className={cn(typographyVariant({ variant: 'caption' }), 'truncate')}
                >
                  {chipLabel}
                </span>
                {extraCount > 0 && (
                  <span
                    className={cn(
                      typographyVariant({ variant: 'caption' }),
                      'shrink-0 tabular-nums text-text-tertiary'
                    )}
                  >
                    {intl.formatMessage(sourceGroundedMessages.extraSources, {
                      count: extraCount,
                    })}
                  </span>
                )}
              </>
            )}
          </button>
        ),
        content: (
          <div className="divide-y divide-border">
            {resolved.map((reference) => (
              <SourceReferenceCard
                key={reference.id}
                reference={reference}
                onOpen={() => openReference(reference)}
                compact={compact}
              />
            ))}
          </div>
        ),
      })}
    </>
  );
};
